import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { api } from '../../lib/api'
import {
  CheckCircle,
  XCircle,
  HelpCircle,
  AlertCircle,
  ChevronLeft,
  ChevronDown,
  ChevronUp,
  BookOpen,
  ThumbsUp,
  Heart,
  Send,
  FileText,
  Camera,
  ChevronRight,
  GraduationCap,
  RefreshCw,
} from 'lucide-react'
import { useState, useEffect } from 'react'

interface Question {
  id: number
  question_number: number
  question_text: string
  student_answer: string | null
  answer_status: string
  ai_assessment: string
  ai_confidence: number
  correct_answer: string
  ai_explanation: string
  skill_name: string | null
  parent_confirmation: string | null
  parent_note: string | null
  source_lesson: string | null
  concept_label: string | null
}

interface HomeworkSession {
  id: number
  student_id: number
  student_name: string
  subject: string
  title: string
  course_name: string | null
  status: string
  homework_date: string | null
  total_questions: number
  correct_count: number
  incorrect_count: number
  skipped_count: number
  uncertain_count: number
  reviewed_by_parent: boolean
  reviewed_at: string | null
  confirmed_needs_help_count: number
  confirmed_got_it_count: number
  used_textbook: boolean
  used_course_materials: boolean
  created_at: string | null
}

interface ReviewData {
  correct: Question[]
  skipped: Question[]
  incorrect: Question[]
  uncertain: Question[]
}

interface ScanInfo {
  scan_id: number
  student_id: number
}

interface CourseFile {
  id: number
  name: string
  url: string | null
  mime_type: string | null
}

interface LessonQuestion {
  question_number: number
  question_text: string
  section_title: string | null
  answer_type: string | null
  correct_answer: string | null
  source_lesson: string | null
  concept_label: string | null
}

interface TeacherGrade {
  score: number | null
  grade: string | null
  points_possible: number | null
  graded_at: string | null
}

interface HomeworkReviewResponse {
  session: HomeworkSession
  review: ReviewData
  scan_info: ScanInfo | null
  course_files: CourseFile[]
  lesson_questions: LessonQuestion[]
  teacher_grade: TeacherGrade | null
}

interface ScanGradingStatusResponse {
  status: string
  session_id?: number
  total_questions?: number
  correct_count?: number
  incorrect_count?: number
  skipped_count?: number
}

type ConfirmationType = 'needs_help' | 'got_it_now' | 'actually_correct'

interface Confirmation {
  question_id: number
  confirmation: ConfirmationType
  note?: string
}

// Multi-page scan image viewer (fetches via authenticated API)
function ScanImageViewer({ scanId, studentId }: { scanId: number; studentId: number }) {
  const [currentPage, setCurrentPage] = useState(1)
  const [pageCount, setPageCount] = useState(1)
  const [loading, setLoading] = useState(true)
  const [blobUrl, setBlobUrl] = useState<string | null>(null)
  const [error, setError] = useState(false)

  // Fetch page info
  useEffect(() => {
    api
      .get(`/api/parent/students/${studentId}/scans/${scanId}/image?info=true`)
      .then((res) => {
        const data = res.data
        const nextPageCount = Math.max(1, data.page_count || 1)
        setPageCount(nextPageCount)
        setCurrentPage((prev) => Math.min(Math.max(1, prev), nextPageCount))
      })
      .catch(() => setPageCount(1))
  }, [scanId, studentId])

  // Fetch image as blob (needs JWT auth)
  useEffect(() => {
    setLoading(true)
    setError(false)
    let nextBlobUrl: string | null = null

    setBlobUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev)
      return null
    })

    api
      .get(`/api/parent/students/${studentId}/scans/${scanId}/image?page=${currentPage}`, {
        responseType: 'blob',
      })
      .then((res) => {
        nextBlobUrl = URL.createObjectURL(res.data)
        setBlobUrl(nextBlobUrl)
        setLoading(false)
      })
      .catch(() => {
        setError(true)
        setLoading(false)
      })
    return () => {
      if (nextBlobUrl) URL.revokeObjectURL(nextBlobUrl)
    }
  }, [scanId, studentId, currentPage])

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-3 py-2 bg-surface-card rounded-t-lg border-b border-gray-700/50">
        <div className="flex items-center gap-2 text-sm text-gray-300">
          <Camera className="w-4 h-4" />
          Student's Work
        </div>
        {pageCount > 1 && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="p-1 rounded hover:bg-surface-hover disabled:opacity-30"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-xs text-gray-400">
              {currentPage} / {pageCount}
            </span>
            <button
              onClick={() => setCurrentPage((p) => Math.min(pageCount, p + 1))}
              disabled={currentPage >= pageCount}
              className="p-1 rounded hover:bg-surface-hover disabled:opacity-30"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
      <div className="flex-1 overflow-auto bg-surface-base rounded-b-lg">
        {loading && (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin w-6 h-6 border-2 border-gray-600 border-t-blue-400 rounded-full" />
          </div>
        )}
        {error && (
          <div className="flex items-center justify-center h-48 text-gray-500 text-sm">
            Could not load scan image
          </div>
        )}
        {blobUrl && (
          <img
            src={blobUrl}
            alt={`Scan page ${currentPage}`}
            className="w-full"
          />
        )}
      </div>
    </div>
  )
}

// Course file PDF viewer — fetches via authenticated API, displays inline
function CourseFileViewer({ files }: { files: CourseFile[] }) {
  const [selectedFileId, setSelectedFileId] = useState<number | null>(files[0]?.id ?? null)
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)

  // Fetch selected file via proxy endpoint (serves from Spaces or Canvas)
  useEffect(() => {
    if (!selectedFileId) return
    setLoading(true)
    setError(false)
    setPdfBlobUrl(null)

    api
      .get(`/api/homework/course-files/${selectedFileId}/content`, { responseType: 'blob' })
      .then((res) => {
        const url = URL.createObjectURL(res.data)
        setPdfBlobUrl(url)
        setLoading(false)
      })
      .catch(() => {
        setError(true)
        setLoading(false)
      })

    return () => {
      if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl)
    }
  }, [selectedFileId])

  if (files.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500 text-sm">
        <div className="text-center">
          <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
          No assignment files available
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-3 py-2 bg-surface-card rounded-t-lg border-b border-gray-700/50">
        <div className="flex items-center gap-2 text-sm text-gray-300">
          <FileText className="w-4 h-4" />
          Assignment
        </div>
        {files.length > 1 ? (
          <select
            value={selectedFileId ?? ''}
            onChange={(e) => setSelectedFileId(Number(e.target.value))}
            className="text-xs bg-gray-700 text-gray-300 rounded px-2 py-1 border-none max-w-[200px]"
          >
            {files.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
        ) : (
          <span className="text-xs text-gray-500 truncate max-w-[200px]">{files[0].name}</span>
        )}
      </div>
      <div className="flex-1 bg-surface-base rounded-b-lg overflow-hidden">
        {loading && (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin w-6 h-6 border-2 border-gray-600 border-t-blue-400 rounded-full" />
          </div>
        )}
        {error && (
          <div className="flex items-center justify-center h-full text-gray-500 text-sm">
            Could not load file
          </div>
        )}
        {pdfBlobUrl && (
          <iframe
            src={pdfBlobUrl}
            className="w-full h-full"
            title="Assignment PDF"
          />
        )}
      </div>
    </div>
  )
}

export default function HomeworkReview() {
  const { sessionId } = useParams<{ sessionId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [reviewStartTime] = useState(Date.now())
  const [confirmations, setConfirmations] = useState<Map<number, Confirmation>>(new Map())
  const [expandedQuestions, setExpandedQuestions] = useState<Set<number>>(new Set())
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false)
  const [regradeScanId, setRegradeScanId] = useState<number | null>(null)

  const { data, isLoading, error } = useQuery<HomeworkReviewResponse>({
    queryKey: ['homework-review', sessionId],
    enabled: Boolean(sessionId) && regradeScanId === null,
    queryFn: async () => {
      const response = await api.get(`/api/homework/homework/${sessionId}`)
      return response.data
    },
  })

  const regradeStatusQuery = useQuery<ScanGradingStatusResponse>({
    queryKey: ['scan-grading-status', regradeScanId],
    enabled: regradeScanId !== null,
    queryFn: async () => {
      const response = await api.get(`/api/homework/scans/${regradeScanId}/status`)
      return response.data
    },
    refetchInterval: (query) => {
      if (!regradeScanId) return false
      const status = query.state.data?.status
      return status && status !== 'processing' ? false : 2000
    },
    retry: false,
  })

  const regradeWithAiMutation = useMutation({
    mutationFn: async () => {
      // First, extract questions from the course file if available
      if (data?.course_files?.[0] && data.lesson_questions.length === 0) {
        // Parse lesson number from title
        const title = data.session.title || ''
        const lessonMatch = title.match(/L\.?\s*(\d+)/i)
        const lessonNumber = lessonMatch ? parseInt(lessonMatch[1]) : null

        if (lessonNumber && data.course_files[0].id) {
          await api.post(`/api/homework/course-files/${data.course_files[0].id}/extract-questions`, {
            lesson_number: lessonNumber,
          })
        }
      }
      // Then re-process the scan
      if (data?.scan_info) {
        const response = await api.post(`/api/homework/scans/${data.scan_info.scan_id}/process`, {
          force: true,
        })
        return response.data
      }
    },
    onSuccess: (result) => {
      if (result?.status === 'processing' && data?.scan_info?.scan_id) {
        setRegradeScanId(data.scan_info.scan_id)
      }
    },
  })

  const confirmMutation = useMutation({
    mutationFn: async (data: { confirmations: Confirmation[]; review_duration_seconds: number }) => {
      const response = await api.post(`/api/homework/homework/${sessionId}/confirm`, data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['homework-review', sessionId] })
      queryClient.invalidateQueries({ queryKey: ['homework-sessions'] })
      queryClient.invalidateQueries({ queryKey: ['homework-summary'] })
      navigate(`/parent/scans`)
    },
  })

  // Build sorted question list and auto-expand questions needing attention
  const allQuestions = data
    ? [...data.review.correct, ...data.review.incorrect, ...data.review.skipped, ...data.review.uncertain]
        .sort((a, b) => a.question_number - b.question_number)
    : []

  useEffect(() => {
    if (data) {
      const needAttention = new Set<number>()
      ;[...data.review.incorrect, ...data.review.skipped, ...data.review.uncertain].forEach((q) => {
        needAttention.add(q.id)
      })
      setExpandedQuestions(needAttention)
    }
  }, [data])

  useEffect(() => {
    if (!regradeScanId || !regradeStatusQuery.data) return

    const { status, session_id: nextSessionId } = regradeStatusQuery.data

    if (status === 'failed') {
      setRegradeScanId(null)
      return
    }

    if (status !== 'processing' && nextSessionId) {
      setRegradeScanId(null)
      queryClient.invalidateQueries({ queryKey: ['homework-sessions'] })
      queryClient.invalidateQueries({ queryKey: ['homework-summary'] })
      navigate(`/parent/scans/${nextSessionId}`, { replace: true })
    }
  }, [regradeScanId, regradeStatusQuery.data, queryClient, navigate])

  const toggleQuestion = (id: number) => {
    setExpandedQuestions((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const setConfirmation = (questionId: number, type: ConfirmationType) => {
    setConfirmations((prev) => {
      const next = new Map(prev)
      const existing = next.get(questionId)
      next.set(questionId, { question_id: questionId, confirmation: type, note: existing?.note })
      return next
    })
  }

  const setConfirmationNote = (questionId: number, note: string) => {
    setConfirmations((prev) => {
      const next = new Map(prev)
      const existing = next.get(questionId)
      if (existing) {
        next.set(questionId, { ...existing, note })
      }
      return next
    })
  }

  const handleSubmit = () => {
    const reviewDuration = Math.floor((Date.now() - reviewStartTime) / 1000)
    confirmMutation.mutate({
      confirmations: Array.from(confirmations.values()),
      review_duration_seconds: reviewDuration,
    })
  }

  const getUnconfirmedCount = () => {
    if (!data) return 0
    const needsConfirmation = [...data.review.incorrect, ...data.review.skipped, ...data.review.uncertain]
    return needsConfirmation.filter((q) => !confirmations.has(q.id)).length
  }

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-64 bg-surface-card rounded"></div>
        <div className="h-40 bg-surface-card rounded-xl"></div>
        <div className="h-60 bg-surface-card rounded-xl"></div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="card text-center py-12">
        <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-400" />
        <p className="text-gray-400">Failed to load homework review</p>
        <Link to="/parent" className="text-quest-primary hover:underline mt-4 inline-block">
          Return to Dashboard
        </Link>
      </div>
    )
  }

  const { session, review } = data
  const needsReview = [...review.incorrect, ...review.skipped, ...review.uncertain]
  const unconfirmedCount = getUnconfirmedCount()
  const hasSidePanel = data.scan_info || data.course_files.length > 0
  const isRegrading = regradeWithAiMutation.isPending || regradeScanId !== null

  return (
    <div className="pb-24">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Link
          to={`/parent/students/${session.student_id}`}
          className="p-2 rounded-lg bg-surface-card hover:bg-surface-hover transition"
        >
          <ChevronLeft className="w-5 h-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold">{session.title || session.subject}</h1>
          <p className="text-gray-400">
            {session.student_name} &middot; {session.subject}
            {session.course_name && ` \u00b7 ${session.course_name}`}
          </p>
        </div>
      </div>

      {/* Two-column layout: left = AI review, right = source docs */}
      <div className={`flex flex-col ${hasSidePanel ? 'lg:flex-row lg:items-start' : ''} gap-6`}>
        {/* LEFT COLUMN: AI Grading Results */}
        <div className="flex-1 min-w-0 space-y-6">
          {/* Summary Card */}
          <div className="card">
            <div className="grid grid-cols-4 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-green-400">{session.correct_count}</div>
                <div className="text-sm text-gray-400">Correct</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-red-400">{session.incorrect_count}</div>
                <div className="text-sm text-gray-400">Incorrect</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-yellow-400">{session.skipped_count}</div>
                <div className="text-sm text-gray-400">Skipped</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-400">{session.uncertain_count}</div>
                <div className="text-sm text-gray-400">Uncertain</div>
              </div>
            </div>

            {/* Source indicators */}
            <div className="mt-4 flex flex-wrap gap-3">
              {data.lesson_questions.length > 0 && (
                <div className="text-sm text-blue-400 flex items-center gap-1.5 bg-blue-500/10 px-2.5 py-1 rounded-full">
                  <BookOpen className="w-3.5 h-3.5" />
                  Verified with {data.lesson_questions.length} textbook questions
                </div>
              )}
              {session.used_textbook && data.lesson_questions.length === 0 && (
                <div className="text-sm text-gray-500 flex items-center gap-1.5">
                  <BookOpen className="w-3.5 h-3.5" />
                  Answers verified using textbook
                </div>
              )}
              {data.teacher_grade && (
                <div className="text-sm text-purple-400 flex items-center gap-1.5 bg-purple-500/10 px-2.5 py-1 rounded-full">
                  <GraduationCap className="w-3.5 h-3.5" />
                  Teacher grade: {data.teacher_grade.score !== null
                    ? `${data.teacher_grade.score}/${data.teacher_grade.points_possible}`
                    : data.teacher_grade.grade}
                </div>
              )}
              {regradeScanId !== null && (
                <div className="text-sm text-quest-primary flex items-center gap-1.5 bg-quest-primary/10 px-2.5 py-1 rounded-full">
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                  Re-grading in background...
                </div>
              )}
              {data.course_files.length > 0 && data.lesson_questions.length === 0 && data.scan_info && (
                <button
                  onClick={() => regradeWithAiMutation.mutate()}
                  disabled={isRegrading}
                  className="text-sm text-quest-primary flex items-center gap-1.5 bg-quest-primary/10 hover:bg-quest-primary/20 px-2.5 py-1 rounded-full transition disabled:opacity-50"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${isRegrading ? 'animate-spin' : ''}`} />
                  {isRegrading ? 'Re-grading...' : 'Re-grade with textbook'}
                </button>
              )}
            </div>
          </div>

          {/* Question List — all questions in order */}
          {allQuestions.length > 0 ? (
            <div className="divide-y divide-gray-700/50 rounded-xl overflow-hidden border border-gray-700/50">
              {allQuestions.map((q) => (
                <QuestionRow
                  key={q.id}
                  question={q}
                  expanded={expandedQuestions.has(q.id)}
                  onToggle={() => toggleQuestion(q.id)}
                  confirmation={confirmations.get(q.id)?.confirmation}
                  confirmationNote={confirmations.get(q.id)?.note}
                  onConfirm={(type) => setConfirmation(q.id, type)}
                  onNoteChange={(note) => setConfirmationNote(q.id, note)}
                />
              ))}
            </div>
          ) : (
            <div className="card text-center py-8">
              <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-400" />
              <h2 className="text-xl font-bold mb-2">All Done!</h2>
              <p className="text-gray-400">
                Your child completed this homework correctly. Great work!
              </p>
            </div>
          )}
        </div>

        {/* RIGHT COLUMN: Assignment PDF + Student Scan — sticky beside questions */}
        {hasSidePanel && (
          <div className="lg:w-[480px] lg:shrink-0 space-y-3 lg:sticky lg:top-0 lg:self-start lg:h-[calc(100vh-5rem)] flex flex-col">
            {/* Assignment PDF — gets more space */}
            {data.course_files.length > 0 && (
              <div className={`border border-gray-700/50 rounded-lg overflow-hidden ${data.scan_info ? 'flex-[3]' : 'flex-1'} min-h-0`}>
                <CourseFileViewer files={data.course_files} />
              </div>
            )}

            {/* Student's Scanned Work */}
            {data.scan_info && (
              <div className={`border border-gray-700/50 rounded-lg overflow-hidden ${data.course_files.length > 0 ? 'flex-[2]' : 'flex-1'} min-h-0`}>
                <ScanImageViewer
                  scanId={data.scan_info.scan_id}
                  studentId={data.scan_info.student_id}
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Submit Bar */}
      {needsReview.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-surface-base border-t border-gray-700/50 p-4 z-40">
          <div className="max-w-2xl mx-auto flex items-center justify-between">
            <div>
              {unconfirmedCount > 0 ? (
                <span className="text-yellow-400">
                  {unconfirmedCount} question{unconfirmedCount !== 1 ? 's' : ''} need confirmation
                </span>
              ) : (
                <span className="text-green-400">All questions confirmed</span>
              )}
            </div>
            <button
              onClick={() => setShowSubmitConfirm(true)}
              disabled={confirmations.size === 0 || confirmMutation.isPending}
              className="btn-primary flex items-center gap-2 disabled:opacity-50"
            >
              <Send className="w-4 h-4" />
              {confirmMutation.isPending ? 'Saving...' : 'Complete Review'}
            </button>
          </div>
        </div>
      )}

      {/* Confirm Dialog */}
      {showSubmitConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-surface-card rounded-xl p-6 max-w-md w-full">
            <h3 className="text-xl font-bold mb-4">Complete Review?</h3>
            <div className="space-y-2 mb-6">
              <div className="flex justify-between">
                <span className="text-gray-400">Needs Help:</span>
                <span className="text-red-400">
                  {Array.from(confirmations.values()).filter((c) => c.confirmation === 'needs_help').length}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Got It:</span>
                <span className="text-green-400">
                  {Array.from(confirmations.values()).filter((c) => c.confirmation === 'got_it_now').length}
                </span>
              </div>
              {Array.from(confirmations.values()).some((c) => c.confirmation === 'actually_correct') && (
                <div className="flex justify-between">
                  <span className="text-gray-400">Was Actually Right:</span>
                  <span className="text-blue-400">
                    {Array.from(confirmations.values()).filter((c) => c.confirmation === 'actually_correct').length}
                  </span>
                </div>
              )}
              {unconfirmedCount > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-400">Not Confirmed:</span>
                  <span className="text-yellow-400">{unconfirmedCount}</span>
                </div>
              )}
            </div>
            <p className="text-gray-400 text-sm mb-6">
              Items marked as "Needs Help" will be saved for test prep review.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowSubmitConfirm(false)}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowSubmitConfirm(false)
                  handleSubmit()
                }}
                className="btn-primary flex-1"
              >
                Complete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

interface QuestionRowProps {
  question: Question
  expanded: boolean
  onToggle: () => void
  confirmation?: ConfirmationType
  confirmationNote?: string
  onConfirm: (type: ConfirmationType) => void
  onNoteChange: (note: string) => void
}

function QuestionRow({ question, expanded, onToggle, confirmation, confirmationNote, onConfirm, onNoteChange }: QuestionRowProps) {
  const isSkipped = question.answer_status === 'skipped' || question.answer_status === 'blank'
  const isCorrect = question.ai_assessment === 'correct'
  const isIncorrect = question.ai_assessment === 'incorrect'
  const needsConfirmation = !isCorrect

  // Status icon and colors
  const StatusIcon = isCorrect ? CheckCircle : isSkipped ? HelpCircle : isIncorrect ? XCircle : AlertCircle
  const iconColor = isCorrect ? 'text-green-400' : isSkipped ? 'text-yellow-400' : isIncorrect ? 'text-red-400' : 'text-gray-400'
  const bgTint = isCorrect
    ? 'bg-green-500/5'
    : isIncorrect
      ? 'bg-red-500/5'
      : isSkipped
        ? 'bg-yellow-500/5'
        : 'bg-gray-500/5'

  // Inline answer display
  const renderAnswer = () => {
    if (isCorrect) {
      return (
        <span className="text-green-400 text-sm truncate max-w-[200px]">
          {question.student_answer || question.correct_answer}
        </span>
      )
    }
    if (isSkipped) {
      return (
        <span className="text-sm">
          <span className="text-yellow-400/70">skipped</span>
          <span className="text-gray-500 mx-1">&rarr;</span>
          <span className="text-green-400">{question.correct_answer}</span>
        </span>
      )
    }
    if (isIncorrect) {
      return (
        <span className="text-sm">
          <span className="text-red-400 line-through">{question.student_answer}</span>
          <span className="text-gray-500 mx-1">&rarr;</span>
          <span className="text-green-400">{question.correct_answer}</span>
        </span>
      )
    }
    // Uncertain
    return (
      <span className="text-sm">
        <span className="text-gray-400">{question.student_answer}</span>
        <span className="text-yellow-400 ml-1">?</span>
      </span>
    )
  }

  return (
    <div className={bgTint}>
      {/* Collapsed row */}
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-white/5 transition"
      >
        <StatusIcon className={`w-4 h-4 shrink-0 ${iconColor}`} />
        <span className="text-sm font-medium text-gray-400 w-8 shrink-0">Q{question.question_number}</span>
        <span className="text-sm text-gray-200 truncate flex-1 min-w-0">
          {question.question_text}
        </span>
        <span className="shrink-0 text-right">{renderAnswer()}</span>
        {needsConfirmation && !expanded && (
          <span className="shrink-0 flex gap-1.5 ml-2">
            {confirmation ? (
              <span
                className={`text-xs px-2 py-0.5 rounded-full ${
                  confirmation === 'needs_help' ? 'bg-red-500/20 text-red-400'
                    : confirmation === 'actually_correct' ? 'bg-blue-500/20 text-blue-400'
                    : 'bg-green-500/20 text-green-400'
                }`}
              >
                {confirmation === 'needs_help' ? 'Help' : confirmation === 'actually_correct' ? 'Was Right' : 'Got It'}
              </span>
            ) : (
              <>
                <span
                  onClick={(e) => { e.stopPropagation(); onConfirm('actually_correct') }}
                  className="text-xs px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-400 hover:bg-blue-500/30 cursor-pointer transition"
                >
                  Was Right
                </span>
                <span
                  onClick={(e) => { e.stopPropagation(); onConfirm('got_it_now') }}
                  className="text-xs px-2 py-0.5 rounded-full bg-green-500/10 text-green-400 hover:bg-green-500/30 cursor-pointer transition"
                >
                  Got It
                </span>
                <span
                  onClick={(e) => { e.stopPropagation(); onConfirm('needs_help') }}
                  className="text-xs px-2 py-0.5 rounded-full bg-red-500/10 text-red-400 hover:bg-red-500/30 cursor-pointer transition"
                >
                  Help
                </span>
              </>
            )}
          </span>
        )}
        {expanded ? (
          <ChevronUp className="w-4 h-4 shrink-0 text-gray-500" />
        ) : (
          <ChevronDown className="w-4 h-4 shrink-0 text-gray-500" />
        )}
      </button>

      {/* Expanded detail */}
      {expanded && (
        <div className="px-4 pb-3 pl-16 space-y-2">
          {/* Full question text */}
          <p className="text-sm text-gray-300">{question.question_text}</p>

          {/* Concept + source badges */}
          {(question.concept_label || question.source_lesson) && (
            <div className="flex flex-wrap gap-1.5">
              {question.concept_label && (
                <span className="inline-block text-xs px-2 py-0.5 rounded-full bg-purple-500/10 text-purple-400">
                  {question.concept_label}
                </span>
              )}
              {question.source_lesson && (
                <span className="inline-block text-xs px-2 py-0.5 rounded-full bg-cyan-500/10 text-cyan-400">
                  From L{question.source_lesson}
                </span>
              )}
            </div>
          )}

          {/* Explanation */}
          {question.ai_explanation && (
            <p className="text-sm text-blue-300/80 italic">{question.ai_explanation}</p>
          )}

          {/* Low confidence warning */}
          {question.ai_confidence < 80 && (
            <p className="text-xs text-gray-500 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              AI confidence: {Math.round(question.ai_confidence)}% — verify this answer
            </p>
          )}

          {/* Confirm buttons + note (only for questions needing review) */}
          {needsConfirmation && (
            <div className="flex items-center gap-2 pt-1">
              <button
                onClick={() => onConfirm('actually_correct')}
                className={`text-xs px-3 py-1 rounded-full flex items-center gap-1 transition ${
                  confirmation === 'actually_correct'
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-700 hover:bg-blue-500/20 text-gray-300'
                }`}
              >
                <CheckCircle className="w-3 h-3" />
                Was Right
              </button>
              <button
                onClick={() => onConfirm('got_it_now')}
                className={`text-xs px-3 py-1 rounded-full flex items-center gap-1 transition ${
                  confirmation === 'got_it_now'
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-700 hover:bg-green-500/20 text-gray-300'
                }`}
              >
                <ThumbsUp className="w-3 h-3" />
                Got It
              </button>
              <button
                onClick={() => onConfirm('needs_help')}
                className={`text-xs px-3 py-1 rounded-full flex items-center gap-1 transition ${
                  confirmation === 'needs_help'
                    ? 'bg-red-500 text-white'
                    : 'bg-gray-700 hover:bg-red-500/20 text-gray-300'
                }`}
              >
                <Heart className="w-3 h-3" />
                Help
              </button>
              {confirmation && (
                <input
                  type="text"
                  placeholder="Add a note..."
                  value={confirmationNote || ''}
                  onChange={(e) => onNoteChange(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  className="flex-1 text-xs bg-gray-800/50 border border-gray-700/50 rounded-full px-3 py-1 text-gray-300 placeholder-gray-600 focus:outline-none focus:border-gray-600"
                />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
